import { styled } from '@mui/material/styles';
import { BuilderCanvas } from './BuilderCanvas';
import { BuilderToolbar } from './BuilderToolbar';
import { ChartConfigSidebar } from './ChartConfigSidebar';

type Props = {
  /** Dashboard name shown as the large page header */
  title?: string;
  /** Called when the user edits the title block inside the canvas */
  onTitleChange?: (title: string) => void;
  /** Called when the user opens version history */
  onShowVersionHistory?: () => void;
};

/**
 * DashboardBuilder — the full-screen dashboard builder UI.
 *
 * Must be mounted inside a `DashboardBuilderProvider` (which provides all
 * required contexts: config, data, event bus, registry, editing, mutation).
 *
 * Layout:
 *   ┌───────────────────────────────────┬──────────┐
 *   │          BuilderToolbar           │          │
 *   ├───────────────────────────────────┤  Sidebar │
 *   │                                   │  (25%)   │
 *   │          BuilderCanvas            │          │
 *   │                                   │          │
 *   └───────────────────────────────────┴──────────┘
 *
 * The sidebar is a sibling of the canvas column so it spans the full height
 * and pushes the canvas to ~75% — preserving widget aspect ratios via RGL's
 * WidthProvider ResizeObserver.
 */
export function DashboardBuilder({ title, onTitleChange, onShowVersionHistory }: Props): React.ReactElement {
  return (
    <BuilderOuter>
      <BuilderColumn>
        <BuilderToolbar title={title} onShowVersionHistory={onShowVersionHistory} />
        <BuilderBody>
          <BuilderCanvas title={title} onTitleChange={onTitleChange} />
        </BuilderBody>
      </BuilderColumn>
      <ChartConfigSidebar />
    </BuilderOuter>
  );
}

/** Outer flex-row: [canvas column] [sidebar] */
const BuilderOuter = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
}));

/** Left column: toolbar + canvas, takes remaining space */
const BuilderColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
}));

const BuilderBody = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
  position: 'relative',
  padding: '10px',
  backgroundColor: theme.palette.background.paper,
}));
